import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  ValidationResult,
  TelnyxEvent,
  CallbackResult,
} from './types';
import { buildCallbackUrl } from './types';
import { getVoiceAttributes } from './voice';
import { interpolateVariables, resolveMessageWithRephrases } from './interpolate';
import type { Rephrases } from './interpolate';

export type SayConfig = {
  message: string | Record<string, string>;
  repeatDigit?: string;
  rephrases?: Rephrases;
};

const REPEAT_GATHER_TIMEOUT_SECONDS = 2;

export function generateSayElements(config: SayConfig, context: TeXMLContext): TeXMLElement[] {
  const languageCode = context.variables._language;
  const visitKey = `_rephrase_${context.nodeId}`;
  const visitCount = parseInt(context.variables[visitKey] || '0', 10);
  const resolved = resolveMessageWithRephrases(
    config.message,
    config.rephrases,
    visitCount,
    languageCode,
    context.languageConfig?.defaultLanguage,
  );
  const message = interpolateVariables(resolved, context.variables, context.speechFormats);
  context.variables[visitKey] = String(visitCount + 1);

  const attributes = getVoiceAttributes(
    context.voiceConfig,
    languageCode,
    context.languageConfig,
    context.elevenLabsApiKeyRef,
  );

  return [
    {
      tag: 'Say',
      attributes,
      content: message,
    },
  ];
}

export function buildRepeatGatherElements(sayElements: TeXMLElement[], callbackUrl: string): TeXMLElement[] {
  return [
    {
      tag: 'Gather',
      attributes: {
        input: 'dtmf',
        numDigits: '1',
        timeout: REPEAT_GATHER_TIMEOUT_SECONDS.toString(),
        action: callbackUrl,
        method: 'POST',
      },
      children: sayElements,
    },
    {
      tag: 'Redirect',
      attributes: { method: 'POST' },
      content: callbackUrl,
    },
  ];
}

function generateTeXML(config: SayConfig, context: TeXMLContext): TeXMLElement[] {
  const sayElements = generateSayElements(config, context);

  if (!config.repeatDigit) {
    return sayElements;
  }

  return buildRepeatGatherElements(sayElements, buildCallbackUrl(context));
}

export function sayCallbackHandler(
  event: TelnyxEvent,
  config: SayConfig,
  _variables: Record<string, string>,
  context: TeXMLContext,
): CallbackResult {
  if (event.Digits === config.repeatDigit) {
    return { elements: generateTeXML(config, context) };
  }

  return { nextHandleId: 'out' };
}

function validateConfig(config: SayConfig): ValidationResult {
  const errors: string[] = [];

  const hasMessage =
    typeof config.message === 'string'
      ? config.message.trim().length > 0
      : Object.values(config.message).some((v) => v.trim().length > 0);

  if (!hasMessage) {
    errors.push('Message is required');
  }

  return { valid: errors.length === 0, errors };
}

export const sayWidgetCore: WidgetCoreDefinition<SayConfig> = {
  id: 'say',
  version: 1,
  name: 'Say',
  description: 'Speak text to the caller using text-to-speech',
  category: 'voice',
  tier: 'standard',

  defaultConfig: {
    message: '',
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: (config: SayConfig) => !!config.repeatDigit,
  },

  validation: {
    validateConfig,
  },
};
